import { Injectable, inject } from '@angular/core';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, take } from 'rxjs';
import {
  selectExplorerSJMapRasterActiveSourceByUUID,
  selectExplorerSJMapRasterLayerByUUID,
} from '../../store/selectors/explorer/explorer-sjmap.selector';
import { TitilerNewService } from '../shared/titiler-new.service';
import type { GeoJSON } from 'geojson';

type BBox = [number, number, number, number];

@Injectable({ providedIn: 'root' })
export class RasterTargetResolverService {
  private store: Store = inject(Store);
  private titiler: TitilerNewService = inject(TitilerNewService);

  async resolveTargetRasterUUID(opts: {
    map: MapLibreMap | null;
    geometry?: GeoJSON.Geometry | null;
    allowedUUIDs?: Array<string>;
    isBaseLayer?: (id: string) => boolean;
    maplibreIdToUUID: (layerId: string) => Promise<string | null>;
  }): Promise<string | null> {
    const { map, geometry, allowedUUIDs, isBaseLayer, maplibreIdToUUID } = opts;
    if (!map) return null;

    const style = map.getStyle();
    const layers = (style?.layers || []).slice().reverse();

    const geomBbox = geometry ? this.getGeometryBbox(geometry) : null;

    for (const layer of layers) {
      if (layer.type !== 'raster') continue;
      const id = layer.id;
      if (!id) continue;
      if (isBaseLayer?.(id)) continue;

      const visibility = map.getLayoutProperty(id, 'visibility');
      if (visibility === 'none') continue;

      const uuid = await maplibreIdToUUID(id);
      if (!uuid) continue;
      if (allowedUUIDs && !allowedUUIDs.includes(uuid)) continue;

      if (!geomBbox) return uuid;

      const bounds = await this.getLayerBoundsBBox(uuid);
      if (!bounds) continue;

      if (this.bboxIntersects(geomBbox, bounds)) {
        return uuid;
      }
    }

    for (const layer of layers) {
      if (layer.type !== 'raster') continue;
      const id = layer.id;
      if (!id) continue;
      if (isBaseLayer?.(id)) continue;

      const visibility = map.getLayoutProperty(id, 'visibility');
      if (visibility === 'none') continue;

      const uuid = await maplibreIdToUUID(id);
      if (!uuid) continue;
      if (allowedUUIDs && !allowedUUIDs.includes(uuid)) continue;

      return uuid;
    }

    return null;
  }

  private async getLayerBoundsBBox(uuid: string): Promise<BBox | null> {
    const rasterLayer = await firstValueFrom(
      this.store.select(selectExplorerSJMapRasterLayerByUUID(uuid)).pipe(
        filter((l) => !!l),
        take(1)
      )
    ).catch((): null => null);

    if (rasterLayer?.bounds) {
      const bbox = this.polygonToBbox(rasterLayer.bounds);
      if (bbox) return bbox;
    }

    const activeSource = await firstValueFrom(
      this.store.select(selectExplorerSJMapRasterActiveSourceByUUID(uuid)).pipe(
        filter((s) => !!s),
        take(1)
      )
    ).catch((): null => null);

    if (activeSource?.source) {
      try {
        const zi = await firstValueFrom(
          this.titiler.zoomToRaster(activeSource.source)
        );
        if (zi?.bounds?.length === 4) return zi.bounds as BBox;
      } catch {}
    }
    return null;
  }

  private polygonToBbox(poly: any): BBox | null {
    try {
      const coords: number[][] = [];
      const collect = (p: any) => {
        if (!p) return;
        if (p.type === 'Polygon')
          p.coordinates.flat(1).forEach((c: number[]) => coords.push(c));
        else if (p.type === 'MultiPolygon')
          p.coordinates.flat(2).forEach((c: number[]) => coords.push(c));
        else if (Array.isArray(p))
          p.flat(2).forEach(
            (c: any) => Array.isArray(c) && c.length >= 2 && coords.push(c)
          );
      };
      collect(poly);
      if (!coords.length) return null;
      const xs = coords.map((c) => c[0]);
      const ys = coords.map((c) => c[1]);
      return [
        Math.min(...xs),
        Math.min(...ys),
        Math.max(...xs),
        Math.max(...ys),
      ];
    } catch {
      return null;
    }
  }

  private getGeometryBbox(geometry: GeoJSON.Geometry): BBox {
    const coords: number[][] = [];
    const collect = (g: GeoJSON.Geometry) => {
      if (g.type === 'Point') coords.push(g.coordinates);
      else if (g.type === 'LineString' || g.type === 'MultiPoint')
        coords.push(...g.coordinates);
      else if (g.type === 'Polygon' || g.type === 'MultiLineString')
        g.coordinates.flat(1).forEach((c: number[]) => coords.push(c));
      else if (g.type === 'MultiPolygon')
        g.coordinates.flat(2).forEach((c: number[]) => coords.push(c));
      else if (g.type === 'GeometryCollection') g.geometries.forEach(collect);
    };
    collect(geometry);
    const xs = coords.map((c) => c[0]);
    const ys = coords.map((c) => c[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  }

  private bboxIntersects(a: BBox, b: BBox) {
    return !(a[0] > b[2] || a[2] < b[0] || a[1] > b[3] || a[3] < b[1]);
  }
}
